import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select,
    FormControl, InputLabel, Autocomplete, Checkbox, Box, FormControlLabel,
    FormLabel, RadioGroup, Radio, Accordion, AccordionSummary, AccordionDetails, Typography, FormHelperText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StyledButton, StyledCancelButton } from '../../StyledComponent';
import { phoneNumberRegex, userHeightRegex } from '../../../regex/regex';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const EditBasicInfoDialog = ({ open, onClose, existingData, userData, setUserData, onSubmit, getWeightData }) => {
    const [touched, setTouched] = useState({
        dateOfBirth: false,
        gender: false,
        alternativeContact: false,
        height: false,
        address: false,
        clinicalIssues: false,
        wakeUpTime: false
    });

    const handleSave = async () => {
        await onSubmit();

        if (getWeightData) {
            getWeightData();
        }
        onClose();

    };
    useEffect(() => {
        if (open && existingData) {
            setUserData((prevData) => ({
                ...prevData,
                ...existingData,
            }));
        }
    }, [open, existingData]);

    const handleBlur = (field) => {
        setTouched((prevTouched) => ({
            ...prevTouched,
            [field]: true,
        }));
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData((prevData) => ({ ...prevData, [name]: value }));
    };
    const handleChangeMensHis = (e) => {
        const { name, value } = e.target;
        setUserData((prevData) => ({ ...prevData, menstrualHistory: { ...prevData.menstrualHistory, [name]: value } }));
    };
    const handleDateChange = (newValue) => {
        setUserData((prevData) => ({ ...prevData, dateOfBirth: newValue }));
    };

    const handleClinicalIssuesChange = (option) => {
        setUserData((prevData) => {
            const currentIssues = prevData.clinicalIssues || [];
            const currentDetails = prevData.clinicalIssuesDetails || {};

            let newIssues;
            let newDetails = { ...currentDetails };

            if (option === "No any issue, that I knew") {
                // If "No any issue" is selected, clear everything else
                newIssues = ["No any issue, that I knew"];
                newDetails = {};
            } else {
                if (currentIssues.includes(option)) {
                    // Deselect
                    newIssues = currentIssues.filter(i => i !== option);
                    delete newDetails[option];
                } else {
                    // Select
                    newIssues = [...currentIssues.filter(i => i !== "No any issue, that I knew"), option];
                }
            }

            return {
                ...prevData,
                clinicalIssues: newIssues,
                clinicalIssuesDetails: newDetails
            };
        });
    };

    const handleIssueDetailChange = (option, value) => {
        setUserData((prevData) => ({
            ...prevData,
            clinicalIssuesDetails: {
                ...(prevData.clinicalIssuesDetails || {}),
                [option]: value
            }
        }));
    };

    const clinicalIssuesOptions = [
        'No any issue, that I knew',
        'Diabetes (insulin dependent)',
        'Diabetes (insulin independent)',
        'Thyroid',
        'PCOS (polycystic ovary syndrome)',
        'Asthama',
        'Others'
    ];


    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle fontWeight='bold'>User Info</DialogTitle>
            <DialogContent>
                <Box display='flex' flexDirection='row' gap={2}>
                    <DatePicker
                        label="Date of Birth"
                        value={userData.dateOfBirth ? dayjs(userData.dateOfBirth) : null}
                        onChange={(newValue) => {
                            setUserData((prevData) => ({
                                ...prevData,
                                dateOfBirth: newValue ? newValue.toISOString() : null,
                            }));
                        }}
                        minDate={dayjs().subtract(100, 'year')}
                        maxDate={dayjs()}
                        slotProps={{
                            textField: {
                                variant: 'standard',
                                fullWidth: true,
                                margin: 'normal',
                                name: 'dateOfBirth',
                                required: true,
                                onBlur: () => handleBlur("dateOfBirth"),
                                error: !userData.dateOfBirth || (touched.dateOfBirth && userData.dateOfBirth && isNaN(Date.parse(userData.dateOfBirth))),
                                helperText: !userData.dateOfBirth
                                    ? "Date of Birth is required."
                                    : touched.dateOfBirth && userData.dateOfBirth && isNaN(Date.parse(userData.dateOfBirth))
                                    ? "Please select a valid date."
                                    : "",
                                InputLabelProps: { shrink: true }
                            }
                        }}
                    />

                    <FormControl fullWidth variant="standard" margin="normal" error={!userData.gender}>
                        <InputLabel required>Gender</InputLabel>
                        <Select
                            name="gender"
                            value={userData.gender}
                            onChange={handleChange}
                            label="Gender"
                        >
                            <MenuItem value="Female">Female</MenuItem>
                            <MenuItem value="Male">Male</MenuItem>
                        </Select>
                        {!userData.gender && <FormHelperText>Gender is required.</FormHelperText>}
                    </FormControl>
                </Box>
                {userData.gender === "Female" && <Box>
                    <Box display='flex' flexDirection='row' gap={2}>
                        <DatePicker
                            label="Last Menstrual Phase (The date on which the last period started.)"
                            value={userData?.menstrualHistory?.lastMenstrualPhase ? dayjs(userData?.menstrualHistory?.lastMenstrualPhase) : null}
                            onChange={(newValue) => {
                                const formattedDate = newValue ? newValue.format('YYYY-MM-DD') : '';
                                handleChangeMensHis({ target: { name: 'lastMenstrualPhase', value: formattedDate } });
                            }}
                            maxDate={dayjs()}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    variant: 'standard',
                                    margin: 'normal',
                                    required: true,
                                    name: 'lastMenstrualPhase',
                                    error: !userData?.menstrualHistory?.lastMenstrualPhase,
                                    helperText: !userData?.menstrualHistory?.lastMenstrualPhase ? "Last Menstrual Phase is required." : "",
                                    InputLabelProps: { shrink: true }
                                }
                            }}
                        />
 
                        <TextField
                            label="Avarage Cycle Length (in days)"
                            fullWidth
                            variant="standard"
                            margin="normal"
                            type='number'
                            required
                            name="avarageCycleLength"
                            value={userData?.menstrualHistory?.avarageCycleLength}
                            onChange={handleChangeMensHis}
                            error={!userData?.menstrualHistory?.avarageCycleLength || (userData?.menstrualHistory?.avarageCycleLength?.length > 0 && (userData?.menstrualHistory?.avarageCycleLength <= 15 || userData?.menstrualHistory?.avarageCycleLength >= 60))}
                            helperText={
                                !userData?.menstrualHistory?.avarageCycleLength 
                                    ? "Average Cycle Length is required."
                                    : (userData?.menstrualHistory?.avarageCycleLength?.length > 0 && (userData?.menstrualHistory?.avarageCycleLength <= 15 || userData?.menstrualHistory?.avarageCycleLength >= 60))
                                    ? "Avarage cycle length should be between 15 to 60 days"
                                    : ""
                            }
                        />
                    </Box>
                    <Box display='flex' flexDirection='row' gap={2} alignItems='center'>
                        <TextField
                            label="Period Flow Length (in days)"
                            fullWidth
                            variant="standard"
                            margin="normal"
                            name="periodFlowLength"
                            required
                            type='number'
                            value={userData?.menstrualHistory?.periodFlowLength ? userData?.menstrualHistory?.periodFlowLength : ''}
                            onChange={handleChangeMensHis}
                            error={!userData?.menstrualHistory?.periodFlowLength || (userData?.menstrualHistory?.periodFlowLength?.length > 0 && (userData?.menstrualHistory?.periodFlowLength <= 1 || userData?.menstrualHistory?.periodFlowLength >= 15))}
                            helperText={
                                !userData?.menstrualHistory?.periodFlowLength
                                    ? "Period Flow Length is required."
                                    : (userData?.menstrualHistory?.periodFlowLength?.length > 0 && (userData?.menstrualHistory?.periodFlowLength <= 1 || userData?.menstrualHistory?.periodFlowLength >= 15))
                                    ? "Period Flow Length should be between 1 to 15 days"
                                    : ""
                            }

                        />
                        <FormControl fullWidth error={!userData?.menstrualHistory?.regular}>
                            <FormLabel id="period-type-label" required>Select Period Type</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby="period-type-label"
                                name="regular"
                                value={userData?.menstrualHistory?.regular}
                                onChange={handleChangeMensHis}
                            >
                                <FormControlLabel value="true" control={<Radio size='small' />} label="Regular" size='small' />
                                <FormControlLabel value="false" control={<Radio size='small' />} label="Irregular" size='small' />
                            </RadioGroup>
                            {!userData?.menstrualHistory?.regular && <FormHelperText>Period Type is required.</FormHelperText>}
                        </FormControl>
                    </Box>
                    <Box>
                        <TextField label="Notes" fullWidth variant='standard'
                            margin="normal"
                            name="notes"
                            value={userData?.menstrualHistory?.notes}
                            onChange={handleChangeMensHis}

                        />
                    </Box>
                </Box>
                }

                <Box display='flex' flexDirection='row' gap={2}>
                    <TextField
                        label="Alternate Contact No."
                        fullWidth
                        variant="standard"
                        margin="normal"
                        name="alternativeContact"
                        value={userData.alternativeContact}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                            userData?.alternativeContact?.length > 0 && !phoneNumberRegex?.test(userData?.alternativeContact)
                        }
                        helperText={
                            userData?.alternativeContact?.length > 0 && !phoneNumberRegex?.test(userData?.alternativeContact)
                                ? "Enter valid mobile no."
                                : ""
                        }
                    />
                    <TextField
                        label="Height (in cm)"
                        fullWidth
                        variant="standard"
                        margin="normal"
                        name="height"
                        required
                        type='number'
                        value={userData.height}
                        onChange={handleChange}
                        onBlur={() => handleBlur("height")}
                        error={!userData.height || (touched.height && (!userData.height || !userHeightRegex.test(userData.height)))}
                        helperText={
                            !userData.height 
                                ? "Height is required." 
                                : touched.height && !userHeightRegex.test(userData.height) 
                                ? "Enter a valid height between 30 cm and 300 cm." 
                                : ""
                        }
                    />
                </Box>
                <TextField
                    label="Address"
                    fullWidth
                    variant="standard"
                    margin="normal"
                    name="address"
                    required
                    value={userData.address}
                    onChange={handleChange}
                    onBlur={() => handleBlur("address")}
                    error={!userData.address || userData.address.trim().length === 0}
                    helperText={
                        !userData.address || userData.address.trim().length === 0
                            ? "Address is required."
                            : ""
                    }
                />

                <Box display='flex' flexDirection='row' gap={2}>
                    <TextField
                        label="Occupation"
                        fullWidth
                        variant="standard"
                        margin="normal"
                        name="occupation"
                        value={userData.occupation}
                        onChange={handleChange}
                    />
                    <TextField
                        label="Organization"
                        fullWidth
                        variant="standard"
                        margin="normal"
                        name="organization"
                        value={userData.organization}
                        onChange={handleChange}
                    />
                </Box>
                <Box display='flex' flexDirection='row' gap={2} alignItems='center' mt={2} mb={2}>
                    <TextField
                        label="Wake Up Time"
                        fullWidth
                        variant="standard"
                        margin="normal"
                        name="wakeUpTime"
                        type="time"
                        value={userData.wakeUpTime ? new Date(userData.wakeUpTime).toISOString().substring(11, 16) : ''}
                        onChange={(e) => {
                            const time = e.target.value;
                            const currentDate = new Date();
                            const [hours, minutes] = time.split(':');
                            currentDate.setUTCHours(hours, minutes, 0, 0);
                            const isoString = currentDate.toISOString();
                            setUserData((prevData) => ({ ...prevData, wakeUpTime: isoString }));
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Box>
                <Box display='flex' flexDirection='column' gap={1}>
                    <FormLabel id="clinical-issues-label" required error={!userData.clinicalIssues || userData.clinicalIssues.length === 0} sx={{ fontWeight: 'bold', mb: 1 }}>Clinical Issues</FormLabel>
                    {clinicalIssuesOptions.map((option) => {
                        const isNoIssue = option === "No any issue, that I knew";
                        return (
                            <Accordion
                                key={option}
                                expanded={isNoIssue ? false : (userData.clinicalIssues || []).includes(option)}
                                disableGutters
                                sx={{
                                    boxShadow: 'none',
                                    '&:before': { display: 'none' },
                                    mb: 0
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={isNoIssue ? null : <ExpandMoreIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClinicalIssuesChange(option);
                                    }}
                                    sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { margin: '4px 0' } }}
                                >
                                    <Box display="flex" alignItems="center" width="100%" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={(userData.clinicalIssues || []).includes(option)}
                                            onChange={() => handleClinicalIssuesChange(option)}
                                            size="small"
                                            sx={{ ml: -1 }}
                                        />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{option}</Typography>
                                        {userData.clinicalIssuesDetails?.[option] && (
                                            <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                                                (Medication: {userData.clinicalIssuesDetails[option]})
                                            </Typography>
                                        )}
                                    </Box>
                                </AccordionSummary>
                                {!isNoIssue && (
                                    <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            label="Add Medication / Details"
                                            variant="standard"
                                            size="small"
                                            value={userData.clinicalIssuesDetails?.[option] || ''}
                                            onChange={(e) => handleIssueDetailChange(option, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            placeholder={`Enter medicine or details for ${option}...`}
                                        />
                                    </AccordionDetails>
                                )}
                            </Accordion>
                        );
                    })}
                    {(!userData.clinicalIssues || userData.clinicalIssues.length === 0) && (
                        <Typography color="error" variant="caption">Please select at least one clinical issue.</Typography>
                    )}

                    <TextField
                        label="Additional Clinical Notes"
                        placeholder="Add anything related to clinical issues..."
                        fullWidth
                        multiline
                        minRows={2}
                        variant="outlined"
                        name="clinicalNotes"
                        sx={{
                            mt: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px'
                            }
                        }}
                        value={userData.clinicalNotes || ''}
                        onChange={handleChange}
                    />
                </Box>

            </DialogContent>
            <DialogActions>
                <StyledCancelButton onClick={onClose}>Cancel</StyledCancelButton>
                <StyledButton onClick={handleSave}
                    disabled={(userData?.dateOfBirth && userData.gender && userData.height && userData.address && (userData.clinicalIssues?.length > 0)
                        &&
                        (userData?.alternativeContact ? phoneNumberRegex?.test(userData?.alternativeContact) : true) && userHeightRegex.test(userData.height) &&
                        (
                            userData.gender !== 'Female' || (userData?.menstrualHistory?.periodFlowLength && userData?.menstrualHistory?.lastMenstrualPhase && userData?.menstrualHistory?.avarageCycleLength && userData?.menstrualHistory?.periodFlowLength && userData?.menstrualHistory?.regular
                                && (!(userData?.menstrualHistory?.avarageCycleLength <= 15) || !(userData?.menstrualHistory?.avarageCycleLength >= 60))
                                && (!(userData?.menstrualHistory?.periodFlowLength <= 1) || (!(userData?.menstrualHistory?.periodFlowLength >= 15)))

                            )
                        ))
                        ? false : true}
                >
                    Save
                </StyledButton>
            </DialogActions>
        </Dialog>
    );
};

export default EditBasicInfoDialog;

